import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Pagination } from './ui/pagination';
import { Plus, Search, Eye, Edit, Trash2, Package, Calendar, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { endgoodApiService, Endgood, formatEndgoodItemTypeLabel } from '../services/endgoodApi';
import { PermissionGate } from './ui/PermissionGate';

export function EndgoodListingPage() {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [parts, setParts] = useState<Endgood[]>([]);
  const [loading, setLoading] = useState(true);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  
  // Server-side pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const pageSize = 20;

  // Endgood management route (from backend permissions)
  const ENDGOOD_ROUTE = '/endgoods';

  // Debounce search term - wait 500ms after user stops typing
  useEffect(() => {
    // Clear existing timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Set new timer
    debounceTimerRef.current = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setCurrentPage(1); // Reset to first page when search changes
    }, 500);

    // Cleanup function
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [searchTerm]);

  // Load parts from API with pagination
  useEffect(() => {
    loadParts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, debouncedSearchTerm]);

  const loadParts = async () => {
    try {
      setLoading(true);
      
      let response;
      if (debouncedSearchTerm) {
        // Search with pagination
        response = await endgoodApiService.searchEndgoodsPaginated(debouncedSearchTerm, currentPage, pageSize);
      } else {
        // Get all with pagination
        response = await endgoodApiService.getEndgoodsPaginated(currentPage, pageSize);
      }
      
      if (response.success) {
        setParts(response.data);
        
        // Handle pagination metadata
        if (response.pagination) {
          setTotalPages(response.pagination.totalPages);
          setTotalCount(response.pagination.totalCount);
        } else {
          // Fallback if pagination metadata is missing
          setTotalPages(1);
          setTotalCount(response.data.length);
        }
      }
    } catch (error) {
      // console.error('Error loading parts:', error);
      toast.error('Failed to load parts');
      setParts([]);
      setTotalPages(1);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    // Note: Page reset is handled in the debounce effect
  };

  const handleNewPart = () => {
    navigate('/endgoods/new');
  };

  const handleEditPart = (part: Endgood) => {
    navigate(`/endgoods/${part.id}/edit`);
  };

  const handleViewPart = (part: Endgood) => {
    navigate(`/endgoods/${part.id}/view`);
  };

  const handleDeletePart = async (part: Endgood) => {
    if (window.confirm(`Are you sure you want to delete ${part.endgoodName}?`)) {
      try {
        await endgoodApiService.deleteEndgood(part.id.toString());
        await loadParts(); // Reload the list
        toast.success('Part deleted successfully');
      } catch (error) {
        // console.error('Error deleting part:', error);
        toast.error('Failed to delete part');
      }
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex items-center gap-2">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
          <span>Loading endgoods...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header Section */}
      <Card className="border-0 shadow-lg bg-white/70 backdrop-blur-sm">
        <CardHeader className="bg-gradient-to-r from-primary-500 to-primary-600 text-white rounded-t-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-8 w-8 rounded-lg bg-white/20 flex items-center justify-center">
                <Package className="h-5 w-5" />
              </div>
              <div>
                <CardTitle className="text-xl">Endgood Management</CardTitle>
                <p className="text-primary-100 text-sm">Manage your endgood information and specifications</p>
              </div>
            </div>
            <PermissionGate route={ENDGOOD_ROUTE} action="create">
              <Button
                onClick={handleNewPart}
                className="bg-white text-primary-600 hover:bg-primary-50 shadow-lg"
              >
                <Plus className="h-4 w-4 mr-2" />
                New Engood
              </Button>
            </PermissionGate>
          </div>
        </CardHeader>
        <CardContent className="p-6">
          {/* Search and Filters */}
          <div className="flex items-center gap-4 mb-6">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <Input
                placeholder="Search engoods..."
                value={searchTerm}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="pl-10 border-primary-200 focus:border-primary-400 focus:ring-primary-400"
              />
            </div>
            <div className="text-sm text-gray-600">
              {debouncedSearchTerm ? `${totalCount} engoods found` : `${totalCount} total engoods`}
            </div>
          </div>

          {/* Engoods Table */}
          <div className="border border-primary-200 rounded-lg overflow-hidden bg-white">
            <Table>
              <TableHeader>
                <TableRow className="bg-primary-50">
                  <TableHead className="font-semibold text-primary-800">Engood Name</TableHead>
                  <TableHead className="font-semibold text-primary-800">Part Number</TableHead>
                  <TableHead className="font-semibold text-primary-800">SKU</TableHead>
                  <TableHead className="font-semibold text-primary-800">Description</TableHead>
                  <TableHead className="font-semibold text-primary-800">Weight (g)</TableHead>
                  <TableHead className="font-semibold text-primary-800">Category</TableHead>
                  <TableHead className="font-semibold text-primary-800">Item Type</TableHead>
                  <TableHead className="font-semibold text-primary-800">Status</TableHead>
                  <TableHead className="font-semibold text-primary-800">Created Date</TableHead>
                  <TableHead className="font-semibold text-primary-800 text-center">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {parts.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10} className="text-center py-8 text-gray-500">
                      {loading ? 'Loading engoods...' : debouncedSearchTerm ? `No engoods found matching "${debouncedSearchTerm}"` : 'No engoods found'}
                    </TableCell>
                  </TableRow>
                ) : (
                  parts.map((part) => (
                    <TableRow key={part.id} className="hover:bg-primary-50/50">
                      <TableCell className="font-medium">{part.endgoodName}</TableCell>
                      <TableCell>{part.partNumber}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className="border-primary-200 text-primary-700">
                          {part.sku}
                        </Badge>
                      </TableCell>
                      <TableCell>{part.description}</TableCell>
                      <TableCell>
                        {part.productionWeight != null
                          ? `${part.productionWeight.toLocaleString('en-IN', {
                              maximumFractionDigits: 0
                            })} g`
                          : 'N/A'}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="border-blue-200 text-blue-700 bg-blue-50">
                          {part.endgoodCategory?.categoryName || 'N/A'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant="outline"
                          className={
                            part.itemType === 'SERVICE'
                              ? 'border-violet-200 text-violet-700 bg-violet-50'
                              : 'border-emerald-200 text-emerald-700 bg-emerald-50'
                          }
                        >
                          {formatEndgoodItemTypeLabel(part.itemType)}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge 
                          variant={part.isActive ? "default" : "secondary"}
                          className={part.isActive ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-800"}
                        >
                          {part.isActive ? 'Active' : 'Inactive'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm text-gray-600">
                          {new Date(part.createdAt).toLocaleDateString()}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <PermissionGate route={ENDGOOD_ROUTE} action="view">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleViewPart(part)}
                              className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                          </PermissionGate>
                          <PermissionGate route={ENDGOOD_ROUTE} action="edit">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEditPart(part)}
                              className="text-green-600 hover:text-green-700 hover:bg-green-50"
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                          </PermissionGate>
                          <PermissionGate route={ENDGOOD_ROUTE} action="delete">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeletePart(part)}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </PermissionGate>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex justify-center mt-6">
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
              />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Form Dialog */}
      {/* Removed dialog-related JSX */}
    </div>
  );
}